import { authorizeRest } from "@/Api";
import createEvent from "./event";
import { redirect, useNavigate } from "react-router-dom";
import { ReactNode, useEffect } from "react";
import { toast } from "sonner";
import { refreshUserData, clearUserData } from "./usePermissions";
import { clearSettings } from "./useSettings";

/**
 * Dados do admin por trás de uma sessão personificada ("acessar como usuário").
 * Só existe em contas abertas pela tela de acesso como usuário.
 */
export type ImpersonationMeta = {
  by: {
    uuid: string;
    name: string;
    email: string;
  };
  expires_at: string | null;
};

export type StoredAccount = {
  user: any;
  token: string;
  impersonation?: ImpersonationMeta | null;
};

export const tryGetAuthStorage = () =>
  JSON.parse(localStorage.getItem("auth") as string) || { authorize: false };

/**
 * O token de personificação expira no backend (3h). Aqui só evitamos manter na
 * lista de contas uma sessão que já não autentica mais.
 */
export const isImpersonationExpired = (
  impersonation?: ImpersonationMeta | null
): boolean => {
  if (!impersonation?.expires_at) return false;

  const expiresAt = new Date(impersonation.expires_at).getTime();

  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
};

const readStoredAccounts = (): StoredAccount[] => {
  try {
    return JSON.parse(localStorage.getItem("multiAccounts") as string) || [];
  } catch {
    return [];
  }
};

export const getStoredAccounts = (): StoredAccount[] => {
  const accounts = readStoredAccounts();
  const valid = accounts.filter((acc) => !isImpersonationExpired(acc?.impersonation));

  if (valid.length !== accounts.length) {
    localStorage.setItem("multiAccounts", JSON.stringify(valid));
  }

  return valid;
};

export const saveAccountToStorage = (
  user: any,
  token: string,
  impersonation: ImpersonationMeta | null = null
) => {
  const accounts = getStoredAccounts();
  const existingIndex = accounts.findIndex((acc) => acc.user?.email === user?.email);

  if (existingIndex >= 0) {
    accounts[existingIndex] = { user, token, impersonation };
  } else {
    accounts.push({ user, token, impersonation });
  }

  localStorage.setItem("multiAccounts", JSON.stringify(accounts));
};

export const removeAccountFromStorage = (email: string) => {
  const accounts = getStoredAccounts();
  const filtered = accounts.filter((acc) => acc.user?.email !== email);
  localStorage.setItem("multiAccounts", JSON.stringify(filtered));
};

export const switchAccount = (email: string) => {
  const accounts = getStoredAccounts();
  const account = accounts.find((acc) => acc.user?.email === email);

  if (account) {
    authorizeUser({
      user: account.user,
      token: account.token,
      impersonation: account.impersonation ?? null,
    });
    window.location.reload();
  }
};

let auth = tryGetAuthStorage();

export const getAuth = () => auth;

export const getImpersonation = (): ImpersonationMeta | null =>
  auth?.impersonation ?? null;

const [useListener, dispatch] = createEvent(getAuth);

export const authorizeUser = ({
  user,
  token,
  impersonation = null,
}: {
  user: any;
  token: string;
  impersonation?: ImpersonationMeta | null;
}) => {
  let clientIdUser: number | undefined;

  if (user?.user_type?.id == 3 || user?.user_type?.id == 2) {
    clientIdUser = user?.client?.id;
  }

  authorizeRest({ token, clientIdUser: clientIdUser?.toString() || "" });

  auth = {
    user,
    impersonation,
    authorize: true,
  };

  localStorage.setItem(
    "auth",
    JSON.stringify({
      user,
      token,
      impersonation,
      authorize: true,
    })
  );

  // Salva a conta no multi-acesso
  saveAccountToStorage(user, token, impersonation);

  // Recarrega dados do usuário com permissões
  console.log("🔄 Chamando refreshUserData após autorização");
  refreshUserData();

  dispatch();
};

/**
 * Encerra a sessão personificada no front: descarta a conta do usuário-alvo e
 * volta para a conta do admin que iniciou o acesso. A revogação do token no
 * backend é feita por quem chama (POST /impersonate/stop).
 */
export const returnToOwnAccount = () => {
  const adminEmail = auth?.impersonation?.by?.email;
  const impersonatedEmail = auth?.user?.email;

  if (impersonatedEmail) {
    removeAccountFromStorage(impersonatedEmail);
  }

  const accounts = getStoredAccounts();
  const admin =
    accounts.find((acc) => acc.user?.email === adminEmail) || accounts[0];

  if (admin?.user?.email) {
    switchAccount(admin.user.email);
    return;
  }

  logout();
  window.location.href = "/login";
};

export const logout = (removeFromMultiAccounts: boolean = false) => {
  if (removeFromMultiAccounts && auth?.user?.email) {
    removeAccountFromStorage(auth.user.email);
  }

  localStorage.removeItem("auth");
  auth = {
    authorize: false,
  };

  // Limpa dados do usuário
  clearUserData();
  clearSettings();

  redirect("/login");
  dispatch();
};

// Restaura autenticação se existir no localStorage
if (auth?.authorize && auth?.token) {
  if (isImpersonationExpired(auth.impersonation)) {
    console.log("⌛ Sessão personificada expirada, voltando para a conta anterior");
    returnToOwnAccount();
  } else {
    console.log("🔐 Restaurando autenticação do localStorage");
    authorizeUser({
      user: auth.user,
      token: auth.token,
      impersonation: auth.impersonation ?? null,
    });
  }
}

export const useAuth = () => useListener();

export const useTypeUser = (
  ...type: number[]
): ((props: () => ReactNode) => ReactNode | false) => {
  const { user } = useAuth();

  return type.includes(user?.user_type_id)
    ? (prop: () => ReactNode) => prop()
    : () => false;
};

export const usePanelUser = () => useTypeUser(1);

// Hook para verificar se o usuário pode editar desconto de cliente
// Permite apenas user_type_id = 2 OU role.name = 'Admin'
export const useCanEditClientDiscount = (): ((props: () => ReactNode) => ReactNode | false) => {
  const { user } = useAuth();

  const isTypeUser2 = user?.user_type_id === 2;
  const isRoleAdmin = user?.role?.name === 'Admin';

  return (isTypeUser2 || isRoleAdmin)
    ? (prop: () => ReactNode) => prop()
    : () => false;
};

export const useMiddleware = (...deps: any[]) => {
  const redirect = useNavigate();

  useEffect(() => {
    if (auth?.authorize) return;

    toast.error("User invalido");
    redirect("/login");
  }, deps);

  return !auth?.authorize;
};
